/**
 * The assistant (mission 8 WP07 T032; FR-001, FR-004 to FR-008, FR-011, FR-013;
 * SAT.1, SAT.2, SAT.5, SAT.7, SAT.8; contracts/assistant-api.md; squad C9, OR-15).
 *
 * Typed wrappers for the five routes. The types follow the example bodies in
 * `backend/internal/httpapi/testdata/m08/` member for member (their SPA copies
 * are `src/test/fixtures/m08/`, pinned by `fixtures-m08.test.ts`).
 *
 * - Every id goes through `idSegment` (run and message ids) or `validRuleId` /
 *   `ruleIdSegment` (rule ids) before any fetch, so a malformed one is an
 *   `InvalidIdError` and nothing is sent (S6, C9).
 * - `ask` builds its body key by key (C-006): the run, the optional rule and the
 *   question as typed. The question travels only in this POST body, never in a
 *   URL, a query key or a log (FR-007, FR-010; TEN.11).
 * - Nothing here touches credits (C-005).
 */
import { api, idSegment, oneOf, ruleIdSegment, validRuleId } from '../api'

/** A cited source (FR-001, FR-012, SAT.2; research §1.10). The SPA builds every href. */
export type AssistantSourceKind = 'static_explanation' | 'crrg' | 'layout_spec'
export const ASSISTANT_SOURCE_KINDS: readonly AssistantSourceKind[] = ['static_explanation', 'crrg', 'layout_spec']

export interface AssistantSource {
  kind: AssistantSourceKind
  ref: string
}

/** A reporter's verdict on one answer (FR-011, SAT.8). */
export type AssistantVerdict = 'up' | 'down'
export const ASSISTANT_VERDICTS: readonly AssistantVerdict[] = ['up', 'down']

/** One answered message (FR-006, FR-007, FR-008). */
export interface AssistantMessage {
  id: string
  run_id: string
  /** Drives the static explanation block (FR-009); null on a general ask. */
  rule_id: string | null
  /** What feedback records (FR-011). */
  rule_ids: string[]
  /** The masked question (FR-007, amendments 7 and 9). */
  question: string
  masked_count: number
  answer: string
  sources: AssistantSource[]
  /** The SAT.7 caption renders under this answer (FR-008). */
  sat7: boolean
  feedback: AssistantVerdict | null
  answered_at: string
}

/** The cycle's usage (FR-005, SAT.5): answered and trimmed since `cycle_start`, pending excluded. */
export interface AssistantUsage {
  used: number
  ceiling: number
  resets_at: string
  cycle_start: string
}

/** POST /v1/assistant/ask. `rule_id` is optional (a general ask, or a rejected run's layout ask). */
export interface AskBody {
  run_id: string
  rule_id?: string
  question: string
}

/** The ask's 200: the stored message and the usage read in the settle transaction, this message included. */
export interface AskResult {
  message: AssistantMessage
  usage: AssistantUsage
}

/** Why a run's conversation is not available (D.2, GEN.4). */
export type ConversationUnavailableReason = 'run-not-ready' | 'run-data-gone'

/** GET /v1/runs/{id}/assistant: newest first, at most 50 per page. */
export interface ConversationPage {
  run_id: string
  available: boolean
  unavailable_reason: ConversationUnavailableReason | null
  trimmed: boolean
  messages: AssistantMessage[]
  next_cursor: string | null
}

/** PUT /v1/assistant/messages/{id}/feedback's 200. */
export interface FeedbackResult {
  message_id: string
  verdict: AssistantVerdict
  updated_at: string
}

/** GET /v1/rules/{id}/latest-run: the tenant's most recent ready, unexpired run where the rule fired (OR-8). */
export interface LatestRun {
  rule_id: string
  run_id: string
  count: number
  completed_at: string
}

export async function ask(body: AskBody): Promise<AskResult> {
  idSegment(body.run_id, 'run id')
  const clean: AskBody = { run_id: body.run_id, question: body.question }
  if (body.rule_id !== undefined) clean.rule_id = validRuleId(body.rule_id)
  return api<AskResult>('/assistant/ask', { method: 'POST', body: JSON.stringify(clean) })
}

export async function usage(): Promise<AssistantUsage> {
  return api<AssistantUsage>('/assistant/usage')
}

export async function conversation(runId: string, cursor?: string): Promise<ConversationPage> {
  const path = `/runs/${idSegment(runId, 'run id')}/assistant`
  return api<ConversationPage>(cursor === undefined ? path : `${path}?${new URLSearchParams({ cursor }).toString()}`)
}

export async function putFeedback(messageId: string, verdict: AssistantVerdict): Promise<FeedbackResult> {
  const path = `/assistant/messages/${idSegment(messageId, 'message id')}/feedback`
  const body = { verdict: oneOf(verdict, ASSISTANT_VERDICTS, 'verdict') }
  return api<FeedbackResult>(path, { method: 'PUT', body: JSON.stringify(body) })
}

export async function latestRun(ruleId: string): Promise<LatestRun> {
  return api<LatestRun>(`/rules/${ruleIdSegment(ruleId)}/latest-run`)
}
